import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, Loader2, User, Building2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTenantBranding } from '../hooks/useTenantBranding';

// Schema supports either email or username login
const loginSchema = z.object({
  identifier: z
    .string()
    .min(1, 'L\'email ou nom d\'utilisateur est requis'),
  password: z
    .string()
    .min(1, 'Le mot de passe est requis')
    .min(6, 'Le mot de passe doit contenir au moins 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// Helper to detect if identifier is email
const isEmail = (identifier: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
};

// Helper to adjust color brightness (for gradient)
const adjustColor = (color: string, amount: number): string => {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.substring(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.substring(2, 4), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.substring(4, 6), 16) + amount));
  return `rgb(${r}, ${g}, ${b})`;
};

export const LoginForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error, clearError, isAuthenticated, getDashboardPath } = useAuth();
  const { branding, isLoading: brandingLoading, error: brandingError } = useTenantBranding();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: '',
      password: '',
    },
  });

  // Already authenticated (e.g. opened /login in a new tab): go straight to the
  // dashboard instead of showing the login form again.
  if (isAuthenticated) {
    return <Navigate to={getDashboardPath()} replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    clearError();
    try {
      // Include tenantId from branding when available (for dev mode with ?tenant= param)
      const tenantId = branding.found ? branding.tenantId : undefined;

      // Determine if identifier is email or username
      if (isEmail(data.identifier)) {
        await login({ email: data.identifier, password: data.password, tenantId });
      } else {
        await login({ username: data.identifier, password: data.password, tenantId });
      }
    } catch {
      // Error is handled by the store
    }
  };

  // Show loading state while fetching branding
  if (brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  // Network problem (no cached branding available): offer a retry instead of
  // wrongly claiming the institution doesn't exist
  if (brandingError === 'Erreur de chargement' && !branding.isDevelopment && !branding.isReserved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto h-16 w-16 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Connexion au serveur impossible</h1>
          <p className="text-gray-600 mb-4">
            Impossible de contacter le serveur NEXEDU pour le moment. Veuillez réessayer dans quelques instants.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Show tenant not found error (only in production, not for reserved subdomains)
  if (brandingError && !branding.isDevelopment && !branding.isReserved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto h-16 w-16 bg-red-100 rounded-xl flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Institution non trouvee</h1>
          <p className="text-gray-600 mb-4">
            Le sous-domaine <code className="bg-gray-200 px-2 py-1 rounded">{window.location.hostname}</code> ne correspond a aucune institution.
          </p>
          <a
            href="https://nexedu.ma"
            className="inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retour a NEXEDU
          </a>
        </div>
      </div>
    );
  }

  // Dynamic styles based on tenant branding (charte graphique)
  const primaryColor = branding.primaryColor || '#1E40AF';
  const secondaryColor = branding.secondaryColor || '#6366F1';

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundColor: primaryColor,
      }}
    >
      <div className="max-w-md w-full">
        {/* Logo and Title - Dynamic based on tenant */}
        <div className="text-center mb-8">
          {branding.found && branding.logoUrl ? (
            // Institution logo - larger and prominent
            <div className="bg-white rounded-2xl p-4 shadow-lg mb-6 inline-block">
              <img
                src={branding.logoUrl}
                alt={branding.name}
                className="h-20 w-auto object-contain"
                onError={(e) => {
                  // Fallback to text logo if image fails
                  const container = (e.target as HTMLImageElement).parentElement;
                  if (container) {
                    container.innerHTML = `<span class="text-4xl font-bold" style="color: ${primaryColor}">${branding.name.charAt(0).toUpperCase()}</span>`;
                  }
                }}
              />
            </div>
          ) : (
            // Default NEXEDU logo or fallback letter
            <div className="mx-auto h-20 w-20 bg-white rounded-2xl flex items-center justify-center shadow-lg mb-6">
              <span className="text-3xl font-bold" style={{ color: primaryColor }}>
                {branding.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <h1 className="text-3xl font-bold text-white">{branding.name}</h1>
          {/* Only show description for institutions that have one, otherwise nothing */}
          {branding.found && branding.description && (
            <p className="mt-2 text-white/80 text-sm">
              {branding.description}
            </p>
          )}
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-6">
            Connexion
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email/Username Field */}
            <div>
              <label
                htmlFor="identifier"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email ou nom d'utilisateur
              </label>
              <div className="relative">
                <input
                  {...register('identifier')}
                  type="text"
                  id="identifier"
                  autoComplete="username"
                  className={`
                    block w-full px-4 py-3 pl-11 rounded-lg border shadow-sm
                    focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                    ${errors.identifier ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                  `}
                  placeholder="votre@email.com ou nom d'utilisateur"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <User className="h-5 w-5" />
                </div>
              </div>
              {errors.identifier && (
                <p className="mt-1 text-sm text-red-600">{errors.identifier.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mot de passe
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  className={`
                    block w-full px-4 py-3 pr-12 rounded-lg border shadow-sm
                    focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                    ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                  `}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                  style={{ accentColor: secondaryColor }}
                />
                <span className="ml-2 text-sm text-gray-600">Se souvenir de moi</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium hover:opacity-80 transition-opacity"
                style={{ color: secondaryColor }}
              >
                Mot de passe oublié ?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="
                w-full flex items-center justify-center gap-2 px-4 py-3
                text-white font-medium rounded-lg
                hover:opacity-90 focus:outline-none focus:ring-2
                focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all
              "
              style={{ backgroundColor: secondaryColor }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Connexion en cours...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Se connecter
                </>
              )}
            </button>
          </form>

        </div>

        {/* Footer - Subtle NEXEDU branding */}
        <div className="mt-6 flex items-center justify-center">
          {branding.found ? (
            // For institutions: subtle "powered by" branding (non-clickable)
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10">
              <span className="text-xs text-white/60">
                Espace numerique par
              </span>
              <span className="text-xs font-semibold text-white/90">
                NEXEDU
              </span>
            </div>
          ) : (
            // For NEXEDU platform itself
            <p className="text-sm text-white/70">
              © {new Date().getFullYear()} NEXEDU. Tous droits reserves.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
